import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import { faCirclePlus, faCircleXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    IonButton,
    IonChip,
    IonContent,
    IonIcon,
    IonLabel,
    IonModal,
    IonPage,
    IonTitle,
    IonToast,
    IonicSlides,
    ToastOptions,
    useIonToast,
} from '@ionic/react';
import '@ionic/react/css/ionic-swiper.css';
import { alertCircleOutline, checkmarkCircleOutline } from 'ionicons/icons';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { each, filter, findIndex, map, reject, size } from 'lodash/fp';
import { DateTime } from 'luxon';
import 'swiper/css';
import 'swiper/css/effect-cards';
import 'swiper/css/keyboard';
import 'swiper/css/pagination';
import { EffectCreative, Keyboard, Pagination } from 'swiper/modules';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';

import './ManualPointing.scss';

import { BottomSlotWarning, getImplicitPlatform } from '../App';
import DayPicker, { DayPickerHandle } from '../components/DayPicker';
import { Header } from '../components/Header';
import { IosHeader } from '../components/IosHeader';
import PointingDetail, { PointingDetailHandle, ViewMode } from '../components/common/PointingDetail';
import {
    adjustMadsPropertiesByTime,
    useAppConfig,
    useEmployees,
    useGetLimitDatePointing,
    useMads,
    useWorkPlaces,
} from '../hooks/useSwr';
import { Pointing, PointingToSaved, pointingFields } from '../types/models';
import { defaultToastOptions, loadingAtom, pointingToEditIdAtom, usersAtom } from '../utils/Global';
import odoo, { saveOrCreateWithoutRead, standardizedOdooPointings, unstandardizedOdooPointing } from '../utils/odoo';
import {
    PointingState,
    cleanDataBeforeSending,
    getMainPointings,
    getMetaData,
    pointingInitialValue,
} from '../utils/odooUtils';
import History, { getFieldsChanged, getLastPointingByState } from './History';

// ManualPointing is the page where the user can manually add a pointing when mode is manual
const ManualPointing: React.FC = () => {
    const title = 'Saisie';

    const [presentToast] = useIonToast();

    const location = useLocation<{ edit?: boolean; from?: string }>();
    const history = useHistory();
    const { mainUser } = useAtomValue(usersAtom);

    const { data: appConfig } = useAppConfig();

    const [hasNew, setHasNew] = useState(false);
    const [pointings, setPointings] = useState<Pointing[]>([]);
    const [showNoEmployeeToast, setShowNoEmployeeToast] = useState(false);

    const [selectedDate, setSelectedDate] = useState<DateTime>(DateTime.now().startOf('day'));

    const [pointingToEditId, setPointingToEditId] = useAtom(pointingToEditIdAtom);
    const [canEditPointing, setCanEditPointing] = useState(false);

    const setLoading = useSetAtom(loadingAtom);

    const swiperRef = useRef<SwiperRef>(null);

    const mainPointings = getMainPointings(pointings);

    const { data: employees, ids: employeeIds } = useEmployees();
    const { data: mads } = useMads();
    const { data: workPlaces } = useWorkPlaces();
    const limitDatePointing = useGetLimitDatePointing();
    const initialPointing = pointingInitialValue(
        (selectedDate as DateTime<true>).toISODate(),
        reject('disabled', adjustMadsPropertiesByTime(mads, selectedDate)),
        workPlaces,
        employees,
    );
    const refDayPicker = useRef<DayPickerHandle | null>(null);
    let classNameList = '';

    const canCreatePointing = () => {
        if (mainUser?.type !== 'employee') {
            return true;
        }

        if (!appConfig.limitPointingByDay) {
            return true;
        }

        if (size(mainPointings) < appConfig.limitPointingByDay) {
            return true;
        }

        return false;
    };

    const getOdooPointing = useCallback(
        async () => {
            setLoading(true);
            swiperRef.current?.swiper?.slideTo(0);
            const employeePointings = await odoo.searchRead(
                'hr.madper.app.pointing.time',
                {
                    fields: pointingFields,
                    domain: pointingToEditId
                        ? [['id', '=', pointingToEditId]]
                        : [
                              ['employee_id', 'in', employeeIds],
                              ['date', '=', selectedDate.toISODate()],
                          ],
                    sort: 'create_date desc',
                },
                true,
            );

            let standardizedPointings: Pointing[] = standardizedOdooPointings(employeePointings);
            const mainPointings = getMainPointings(standardizedPointings);

            // check if the user can edit the pointing
            setCanEditPointing(selectedDate.startOf('day') >= limitDatePointing);

            // add a new pointing if there is no pointing for the selected date
            if (canEditPointing && size(mainPointings) === 0) {
                standardizedPointings = [initialPointing];
            }

            if (pointingToEditId) {
                setSelectedDate(DateTime.fromISO(standardizedPointings[0].date).startOf('day'));
            }

            setPointings(standardizedPointings);

            // check if there is a new pointing
            setHasNew(findIndex({ id: 0 })(mainPointings) !== -1);
            setLoading(false);
        },
        // JSON.stringify is used to avoid infinite loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pointingToEditId || selectedDate, canEditPointing, JSON.stringify(employeeIds), appConfig?.appMode],
    );

    useEffect(() => {
        if (!size(employees)) {
            setShowNoEmployeeToast(true);
        }
    }, [employees]);

    useEffect(() => {
        getOdooPointing();
    }, [getOdooPointing]);

    useEffect(() => {
        swiperRef.current?.swiper[hasNew ? 'disable' : 'enable']();
    }, [hasNew]);

    useEffect(() => {
        if (location.pathname === '/saisiedetail' && !pointingToEditId) {
            history.replace(location.state?.from || '/validation');
        }
        if (pointingToEditId && !location.state?.edit) {
            setPointingToEditId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    if (location.pathname !== '/saisiedetail') {
        classNameList += ' left-offset-10';
    }

    return (
        <IonPage>
            <IosHeader title={title} backText={location.state?.edit ? 'Retour' : ''} />
            <IonContent className="content-saisie-d" fullscreen={true}>
                <Header title={title} backText={location.state?.edit ? 'Retour' : ''} />
                <IonToast
                    {...defaultToastOptions}
                    isOpen={showNoEmployeeToast}
                    message="Aucun employé ne vous est associé"
                    color="warning"
                />

                <DayPicker
                    ref={refDayPicker}
                    date={selectedDate}
                    onChange={(date) => {
                        setSelectedDate(date);
                    }}
                    employees={employeeIds}
                    disabled={!!pointingToEditId || (hasNew && size(pointings) > 1)}
                />
                <div className="main-content-d pt-4">
                    <Swiper
                        className={classNameList}
                        slidesPerView={
                            getImplicitPlatform() == 'web' && mainPointings.length >= 2 && window.innerWidth > 1200
                                ? 2
                                : 1
                        }
                        spaceBetween={30}
                        ref={swiperRef}
                        grabCursor={true}
                        pagination={{
                            clickable: true,
                            dynamicBullets: true,
                        }}
                        keyboard={{
                            pageUpDown: true,
                        }}
                        modules={[EffectCreative, Pagination, Keyboard, IonicSlides]}
                    >
                        {mainPointings.length ? (
                            map(
                                (pointing) => (
                                    <SwiperSlide key={`${pointing.date}_${pointing.id}`}>
                                        <ManualPointingChild
                                            pointingValue={pointing}
                                            pointingsHistory={filter({ origin_id: pointing.id }, pointings)}
                                            hasNew={hasNew}
                                            canEditPointing={canEditPointing}
                                            swiperRef={swiperRef.current}
                                            onCreated={() => {
                                                if (!canCreatePointing()) {
                                                    presentToast({
                                                        ...defaultToastOptions,
                                                        message: `Le nombre maximal de pointages pour aujourd'hui a été atteint. La limite étant de ${appConfig.limitPointingByDay} pointages par jour. Cependant vous pouvez modifier le pointage existant.`,
                                                        color: 'warning',
                                                        duration: 6000,
                                                    });
                                                    return;
                                                }

                                                setPointings([initialPointing, ...pointings]);
                                                setHasNew(true);
                                                swiperRef.current?.swiper.slideTo(0);
                                            }}
                                            onValidated={() => {
                                                presentToast({
                                                    ...defaultToastOptions,
                                                    message: `${
                                                        pointing.id > 0
                                                            ? 'Le pointage a bien été modifié'
                                                            : 'Le pointage a bien été ajoutée'
                                                    }${mainUser?.type === 'adherent' ? ' et validé' : ''}`,
                                                });
                                                refDayPicker.current?.refresh();
                                                getOdooPointing();
                                                if (location.pathname === '/saisiedetail') {
                                                    history.replace(location.state?.from || '/validation');
                                                }
                                            }}
                                            onCanceled={() => {
                                                swiperRef.current?.swiper.slidePrev();
                                                setPointings(filter((p) => p.id !== 0, pointings));
                                                setHasNew(false);
                                            }}
                                        />
                                    </SwiperSlide>
                                ),
                                mainPointings,
                            )
                        ) : (
                            <div className="grid h-[50vh] place-items-center">
                                <IonTitle className="text-center text-gray-500">Aucun pointage</IonTitle>
                            </div>
                        )}
                    </Swiper>
                </div>
            </IonContent>
            <BottomSlotWarning />
        </IonPage>
    );
};

export const ManualPointingChild: React.FC<{
    pointingValue: Pointing;
    pointingsHistory: Pointing[];
    hasNew: boolean;
    canEditPointing: boolean;
    swiperRef: SwiperRef | null;
    onCreated: () => void;
    onValidated?: () => void;
    onCanceled?: () => void;
}> = ({ pointingValue, pointingsHistory, hasNew, canEditPointing, swiperRef, onValidated, onCreated, onCanceled }) => {
    const { mainUser } = useAtomValue(usersAtom);
    const pointingToEditId = useAtomValue(pointingToEditIdAtom);
    const [presentToast] = useIonToast();

    const setLoading = useSetAtom(loadingAtom);
    const [isEdited, setIsEdited] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const refPointingDetail = useRef<PointingDetailHandle | null>(null);
    const location = useLocation();
    const pointingWasEdited =
        size(
            getFieldsChanged(
                getLastPointingByState(PointingState.WaitValidation, pointingsHistory, pointingValue.id),
                pointingValue,
            ),
        ) > 0;
    const modal = useRef<HTMLIonModalElement>(null);

    const onConfirm = async () => {
        if (!refPointingDetail.current) {
            return;
        }

        try {
            setLoading(true);
            const pointing = refPointingDetail.current.getPointing();

            const extraData = await getMetaData();

            await saveOrCreateWithoutRead<PointingToSaved>(
                'hr.madper.app.pointing.time',
                unstandardizedOdooPointing({
                    ...cleanDataBeforeSending(pointing),
                    metadata: { ...pointing.metadata, confirm: { ...pointing.metadata.start, ...extraData } },
                }),
                { forwardIp: true, pointingState: 'confirm', bypassValidation: true }, //TODO: Enlever forwardIp de tous les pages pointing
            );

            setIsEdited(false);
            onValidated?.();
        } catch (e: any) {
            each((errorToast: ToastOptions) => presentToast(errorToast), e.messages);
        } finally {
            setLoading(false);
        }
    };

    const openHistoryModal = () => {
        swiperRef?.swiper?.keyboard?.disable();
        modal.current?.present();
    };

    const closeHistoryModal = () => {
        swiperRef?.swiper?.keyboard?.enable();
        modal.current?.dismiss();
    };

    return (
        <>
            <div className="absolute right-5 top-0 mt-1 z-20 text-red-500">
                <div className="flex items-center h-8">
                    {(pointingValue.state === PointingState.AdherentValidation ||
                        pointingValue.state === PointingState.GeiqValidation) && (
                        <>
                            <IonChip color={pointingWasEdited ? 'warning' : 'success'} onClick={openHistoryModal}>
                                <IonIcon icon={pointingWasEdited ? alertCircleOutline : checkmarkCircleOutline} />
                                <IonLabel>
                                    {pointingValue.state === PointingState.AdherentValidation ? 'Adhérent' : 'GEIQ'}
                                </IonLabel>
                            </IonChip>
                            <IonModal ref={modal} className="fullscreen-modal">
                                <History
                                    pointingId={pointingValue.id}
                                    dismiss={closeHistoryModal}
                                    pointingsHistory={pointingsHistory}
                                    viewMode={mainUser?.type === 'adherent' ? ViewMode.Adherent : ViewMode.Manual}
                                />
                            </IonModal>
                        </>
                    )}

                    {canEditPointing && !pointingToEditId && !hasNew && pointingValue.id !== 0 && (
                        <FontAwesomeIcon icon={faCirclePlus} size="lg" onClick={onCreated} />
                    )}
                    {!pointingToEditId &&
                        hasNew &&
                        !(size(swiperRef?.swiper?.slides) === 1 && pointingValue.id === 0) && (
                            <FontAwesomeIcon icon={faCircleXmark} size="lg" onClick={onCanceled} />
                        )}
                </div>
            </div>
            <div className="mt-4">
                <PointingDetail
                    initialPointing={pointingValue}
                    viewMode={mainUser?.type === 'adherent' ? ViewMode.Adherent : ViewMode.Manual}
                    isDisabled={!pointingValue._access_right.write}
                    onChange={(pointing: Pointing) => {
                        if (!isEdited) {
                            setIsEdited(true);
                        }
                        setIsValid(
                            !!(
                                (pointing.mad_id > 0 || (pointing.mad_id === 0 && pointing.other_mad)) &&
                                (pointing.workplace_id > 0 || (pointing.workplace_id === 0 && pointing.other_workplace))
                            ),
                        );
                    }}
                    ref={refPointingDetail}
                />
            </div>

            {pointingValue._access_right.write && (
                <div className="madper-button">
                    <IonButton
                        shape="round"
                        type="submit"
                        disabled={!isEdited || !isValid}
                        expand="block"
                        onClick={onConfirm}
                        className="btn"
                    >
                        {location.pathname === '/saisiedetail' ? 'Valider' : pointingValue.id ? 'Sauvegarder' : 'Créer'}
                    </IonButton>
                </div>
            )}
        </>
    );
};
export default ManualPointing;
